import {
  Component,
  signal,
  input,
  computed,
  inject,
  model,
  Optional,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  CartItemOptionItems,
  getImageCdnUrl,
  ProductOptionItem,
  SUPPORTED_LANGUAGES,
  PurchaseOptions,
} from '@pedix-workspace/utils';

import { AsyncPipe } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';
import { ZoomImage } from '@pedix-workspace/angular-ui-image-zoom';
import { OptionConfigurationsByTypePipe } from '@pedix-workspace/pedixapp-shared-pipes';
import { ApiV1_Establishment, ApiV1_Product } from '@pedix-workspace/api-v1';
import { StockInfoPipe, AvailableStockComponent } from '@pedix-workspace/pedixapp-stock';
import { PropInSelectedLanguagePipe } from '@pedix-workspace/pedixapp-shared-pipes';
import { RichTextComponent } from '@pedix-workspace/angular-ui-rich-text';
import { ImageZoomComponent } from '@pedix-workspace/angular-ui-image-zoom';
import { ImageGalleryComponent } from '@pedix-workspace/angular-ui-image-gallery';
import { InputTextareaComponent } from '@pedix-workspace/angular-ui-form-reactive';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { IconLoadingComponent } from '@pedix-workspace/angular-ui-icons';
import { ProductOptionSelectorComponent } from '../product-option-selector';
import { ProductOptionSelectorWrapperComponent } from '../product-option-selector/product-option-selector-wrapper/product-option-selector-wrapper.component';
import { ProductDetailItemPriceComponent } from '../product-detail-item-price/product-detail-item-price.component';
import { ProductRestrictionTagsComponent } from '../product-restriction-tags/product-restriction-tags.component';
import { ProductDetailsPromotionTagsComponent } from './product-details-promotion-tags/product-details-promotion-tags.component';
import { EnvironmentService } from '@pedix-workspace/angular-utils';
import { PromotionInput } from '@pedix-workspace/shared-promotions';
import { ProductDetailsType } from './product-details-type';

@Component({
  selector: 'pxw-product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    ImageGalleryComponent,
    ImageZoomComponent,
    AvailableStockComponent,
    RichTextComponent,
    ProductOptionSelectorComponent,
    InputTextareaComponent,
    AsyncPipe,
    PropInSelectedLanguagePipe,
    StockInfoPipe,
    FormsModule,
    IconLoadingComponent,
    OptionConfigurationsByTypePipe,
    ProductOptionSelectorWrapperComponent,
    ProductDetailItemPriceComponent,
    ProductDetailsPromotionTagsComponent,
    ProductRestrictionTagsComponent,
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      deps: [[Optional, NgForm]],
      useFactory: (ngForm: NgForm) => ngForm,
    },
  ],
})
export class ProductDetailsComponent {
  private environmentService = inject(EnvironmentService);

  catalogConfiguration = input.required<ApiV1_Establishment['catalogConfiguration']>();
  product = input.required<ApiV1_Product>();
  productType = input.required<ProductDetailsType>();
  selectedLanguage = input.required<SUPPORTED_LANGUAGES>();
  cartItemId = input<string>();
  displaySku = input(false);
  loadingOptions = input(false);
  isFormSubmitted = input(false);
  displayProductPriceWithoutTaxes = input(false);
  promotions = input<PromotionInput[]>([]);
  purchaseOptions = input<PurchaseOptions | null>(null);

  productObservations = model('');
  selectedOptions = model<CartItemOptionItems[]>([]);
  selectedPresentationOption = model<CartItemOptionItems | null>(null);

  imageToZoom = signal<ZoomImage | null>(null);
  imageToZoomTitle = signal('');
  isZoomOpen = signal(false);
  activeGalleryIndex = signal(0);

  galleryImages = computed(() => this.product().images.map(image => this.getGalleryImages(image)));
  hasPresentations = computed(() => this.productType() === 'with-presentations');
  allPresentationsHidden = computed(
    () => this.hasPresentations() && !((this.product().presentations?.items?.length ?? 0) > 0),
  );

  onImageZoom(zoomImage: ZoomImage | null) {
    this.imageToZoom.set(zoomImage);
    this.imageToZoomTitle.set(this.product().name);
    this.isZoomOpen.set(true);
  }

  onGalleryImageChange(galleryIndex: number) {
    this.imageToZoom.set(this.galleryImages()[galleryIndex]);
  }

  onItemImageClick(item: ProductOptionItem) {
    if (item.image) {
      this.imageToZoom.set(this.getGalleryImages(item.image));
      this.isZoomOpen.set(true);
      this.imageToZoomTitle.set(item.name);
    }
  }

  onOptionSelected(option: CartItemOptionItems | undefined, index: number) {
    this.selectedOptions.set(Object.assign([], this.selectedOptions(), { [index]: option }));
  }

  private getGalleryImages(image: string) {
    return {
      large: getImageCdnUrl(image, { size: '2000x2000' }, this.environmentService.env),
      medium: getImageCdnUrl(image, { size: '800x800' }, this.environmentService.env),
      thumb: getImageCdnUrl(image, { size: '250x250' }, this.environmentService.env),
    };
  }
}
